// 付箋・テキスト・図形ラベルなどの編集モードを担うフック。
//
// 振る舞い:
// - ダブルクリックで編集モードに遷移し、対象要素を contentEditable にする
// - IME（CompositionEvent）中は op を送らず、composition 確定後に変更を反映
// - キー入力に対しては 50ms debounce で object.text.update op を送信する
// - blur で編集モードを終了する
//
// 50ms debounce は同時編集体験を担保するためのバランス（README §11）。
//
// markdown オプション（付箋・テキスト図形で true）:
// - 編集中も Markdown 記法（見出し・箇条書き）を即時 WYSIWYG 反映する（マーカー文字は画面から消える）。
// - 保存は生 Markdown のまま。編集領域 DOM は「1 行 = 1 ブロック」の正準構造とし、記法マーカーは
//   data-prefix（隠し）+ ::before（グリフ）へ逃がす（markdown-wysiwyg.ts）。
// - Enter/削除/貼付/カット/Undo などの構造変更は beforeinput / clipboard で横取りし、
//   「生テキスト + 選択の純粋変換 → 正準ブロック再構築」に統一して data-prefix を壊さない。
// - Undo/Redo は自前スナップショットスタックが正本（ネイティブ委任は DOM/IME 最適化であって履歴とは無関係）。
// - markdown を渡さない種別（セクション等）は従来コードパスと完全一致（退行なし）。

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";
import { findUrlTokenAt, isHttpUrl } from "../../../lib/linkify-text";
import { htmlFragmentToLinkText } from "../../../lib/paste-links";
import { BULLET_MAX_LEVEL } from "../../../lib/markdown-text";
import {
  buildMarkdownEditorDom,
  changeBulletIndent,
  deleteBackward,
  deleteForward,
  deleteRange,
  editorNeedsRebuild,
  insertPlainLineBreak,
  insertTextAt,
  isBackspaceBoundary,
  isDeleteForwardBoundary,
  rawSelectionFromRange,
  restoreEditorCaretOffset,
  saveEditorCaretOffset,
  saveEditorSelection,
  serializeMarkdownEditorDom,
  splitForEnter,
  type EditorSelection,
  type EditorTextAndCaret,
} from "../../../lib/markdown-wysiwyg";

interface Options {
  readonly objectId: string;
  readonly initialText: string;
  readonly elementRef: RefObject<HTMLElement | null>;
  readonly dispatcher: CanvasOpDispatcher;
  /** ロック中など編集を抑止したい場合に true を渡す。 */
  readonly disabled?: boolean | undefined;
  /**
   * 本文が空（トリム後 0 文字）で編集を確定したときに呼ばれる。テキスト図形の「0 文字なら自動削除」に使う。
   * このコールバックを渡さない種別（付箋など）では、空でも削除しない。
   */
  readonly onEmptyCommit?: (() => void) | undefined;
  /**
   * 編集中に本文が変化したとき（入力・IME 確定ごと）に、その時点の生テキストで呼ばれる。
   * テキスト図形のように「入力中も本文量に応じて表示（図形枠の自動拡張など）を追従させたい」
   * 種別が、確定を待たずに再計算するために使う。渡さない種別では何も起きない。
   * IME 変換中（composition 中）は呼ばれず、確定時のみ発火するためジッターしない。
   */
  readonly onLiveText?: ((text: string) => void) | undefined;
  /**
   * 付箋・テキスト図形の本文を編集中も Markdown 記法で WYSIWYG 整形する（マーカーは非表示）。
   * 未指定（セクション等）では従来どおり生テキストをそのまま編集する。
   */
  readonly markdown?: boolean | undefined;
  /**
   * リンク付き HTML の貼り付けを [表示テキスト](URL) へ変換して取り込むか。
   * 非編集表示が LinkifiedText（Markdown 形式リンクを描画できる）を使う種別（付箋・テキスト）だけが
   * opt-in する。素のテキスト描画の種別（セクションタイトル等）で有効にすると、変換結果の
   * [ラベル](URL) が生の記法のまま見えてしまうため既定は無効。
   */
  readonly preserveHtmlAnchorsOnPaste?: boolean | undefined;
}

interface Result {
  readonly isEditing: boolean;
  readonly enterEditMode: () => void;
  readonly leaveEditMode: () => void;
  readonly editorProps: {
    readonly contentEditable: boolean;
    readonly suppressContentEditableWarning: boolean;
    readonly onInput: (event: React.FormEvent<HTMLElement>) => void;
    readonly onCompositionStart: () => void;
    readonly onCompositionEnd: (event: React.CompositionEvent<HTMLElement>) => void;
    readonly onBlur: () => void;
    readonly onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    readonly onKeyUp: () => void;
    readonly onClick: (event: React.MouseEvent<HTMLElement>) => void;
    readonly onPaste: (event: React.ClipboardEvent<HTMLElement>) => void;
    readonly onCut: (event: React.ClipboardEvent<HTMLElement>) => void;
    readonly onDrop: (event: React.DragEvent<HTMLElement>) => void;
    readonly onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  };
}

/** Undo/Redo スナップショット（生テキスト + キャレット）。 */
interface UndoSnapshot {
  readonly text: string;
  readonly caret: number;
}

/** 直近の履歴操作から本間隔（ms）以内の 2 回目の Undo/Redo は二重発火として無視する。 */
const HISTORY_DEDUP_MS = 20;

/**
 * IME 変換確定（compositionend）直後の本間隔（ms）以内に届く Enter / 改行入力は、変換確定の Enter が
 * isComposing=false で漏れてきたものとみなして無視する（箇条書きの誤継続/終了を防ぐ）。ブラウザ差の取りこぼし対策。
 */
const COMPOSITION_ENTER_GUARD_MS = 50;

const DEBOUNCE_MS = 50;
// 編集中の自分のキャレット位置を他参加者へ中継する最小送信間隔（ms）。
// use-cursor-broadcaster と同じ約 10Hz に間引いて通信量を抑える。
const CARET_BROADCAST_INTERVAL_MS = 100;

export function useObjectTextEditor(options: Options): Result {
  const { objectId, initialText, elementRef, dispatcher, disabled, onEmptyCommit, onLiveText } =
    options;
  const isMarkdown = options.markdown ?? false;
  const preserveHtmlAnchorsOnPaste = options.preserveHtmlAnchorsOnPaste ?? false;
  // 参照専用（埋め込み）ではテキスト編集（ダブルクリックでの contentEditable 遷移・updateText 送信）を
  // 抑止する。ロック（disabled）と同様に扱う。
  const readOnly = useCanvasStore((s) => s.readOnly);
  const isDisabled = (disabled ?? false) || readOnly;
  const [isEditing, setEditing] = useState(false);
  // leaveEditMode のガード用に編集状態を同期的にも保持する。
  // 非編集時の表示は React 管理の要素（LinkifiedText の <a> / <span>）で描画されるが、
  // <a> はフォーカス可能なため、リンククリック→新規タブ遷移などで「編集していないのに」
  // focusout（onBlur）や Escape の keydown がエディタ要素から発火し得る。そのまま
  // leaveEditMode を実行すると el.textContent = "" が React 管理下の子ノードを直接消し、
  // 表示消失と、後続 reconcile での removeChild 例外（NotFoundError→エラー画面）を招く。
  const isEditingRef = useRef(false);
  const composingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 同時編集の収束用: 「最後に自分から送信した / リモートから適用した」テキスト。
  // 編集中に他者の確定で initialText が変わったとき、ローカルの現在値がこの値と一致していれば
  // （＝未送信のローカル変更が無ければ）リモートの最新テキストを安全に適用できる、という判定に使う。
  const lastSyncedTextRef = useRef(initialText);

  // Undo/Redo（markdown モードの正本）。編集開始時にクリアする。
  const undoRef = useRef<UndoSnapshot[]>([]);
  const redoRef = useRef<UndoSnapshot[]>([]);
  // 連続タイプを 1 ステップへコアレスするための直前オペ種別。
  const lastOpKindRef = useRef<"none" | "type" | "struct">("none");
  // Undo/Redo 適用中はスナップショットを積まない（二重記録防止）。
  const applyingHistoryRef = useRef(false);
  // keydown と beforeinput の両方から Undo/Redo が来たときの二重発火防止用タイムスタンプ。
  const lastHistoryTsRef = useRef(0);
  // IME 変換確定（compositionend）時刻。直後に漏れてくる Enter を抑止するために使う。
  const compositionEndedAtRef = useRef(0);
  // 直前の keydown が IME 変換中に発火したものか（markdown のみ使用）。keydown は対応する
  // beforeinput より必ず先行するため、beforeinput 側はこれで「変換確定の Enter が実 Enter として
  // 漏れてきた改行入力」を壁時計（COMPOSITION_ENTER_GUARD_MS）に依存せず識別できる。
  // 記録の有効期間はその物理キーストロークが生むイベント列まで（keyup で消費する。onKeyUp 参照）。
  const lastKeydownWasImeRef = useRef(false);

  // ---- テキスト読取・反映・キャレットの戦略（markdown 有無で差し替え）----
  const readEditor = useCallback(
    (el: HTMLElement): string => (isMarkdown ? serializeMarkdownEditorDom(el) : readEditorText(el)),
    [isMarkdown]
  );
  const writeEditor = useCallback(
    (el: HTMLElement, text: string): void => {
      if (isMarkdown) buildMarkdownEditorDom(el, text);
      else el.textContent = text;
    },
    [isMarkdown]
  );
  const saveCaret = useCallback(
    (el: HTMLElement): number | null =>
      isMarkdown ? saveEditorCaretOffset(el) : saveCaretCharacterOffset(el),
    [isMarkdown]
  );
  const restoreCaret = useCallback(
    (el: HTMLElement, offset: number): void => {
      if (isMarkdown) restoreEditorCaretOffset(el, offset);
      else restoreCaretCharacterOffset(el, offset);
    },
    [isMarkdown]
  );

  // 無効化された場合はその瞬間に編集モードを終了し、保留中の debounce timer も破棄する。
  // 他クライアントから locked=true が反映された直後に古い text を送信して
  // ロック後のオブジェクトを書き換えてしまうのを防ぐ（dispatcher 側にも防御を入れている）。
  useEffect(() => {
    if (!isDisabled) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (isEditing) {
      // leaveEditMode と同じ理由で、非編集表示へ戻す前に contentEditable の未追跡 DOM をクリアする
      // （ロック等で編集が強制終了する経路でも二重化しないようにする）。
      if (elementRef.current) elementRef.current.textContent = "";
      isEditingRef.current = false;
      setEditing(false);
    }
  }, [isDisabled, isEditing, elementRef]);

  const flushPending = useCallback(
    (text: string) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // 送信した内容を「同期済み」として記録する。以降にサーバからエコーで戻ってくる同じ
      // initialText を、収束処理が「変化なし」として無視できるようにする。
      lastSyncedTextRef.current = text;
      dispatcher.updateText(objectId, text);
    },
    [dispatcher, objectId]
  );

  const scheduleSend = useCallback(
    (text: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        lastSyncedTextRef.current = text;
        dispatcher.updateText(objectId, text);
      }, DEBOUNCE_MS);
    },
    [dispatcher, objectId]
  );

  // 他者の最新テキストを、キャレットをできるだけ保ったまま編集領域へ反映する（同時編集の収束時）。
  // markdown モードでは WYSIWYG 再構築、それ以外は textContent 置換。キャレット保存・復元は失敗しても
  // 本文反映は完了させ、キャレット復元だけを諦める（編集不能に陥らせない）。
  const applyRemoteText = useCallback(
    (el: HTMLElement, text: string): void => {
      if (readEditor(el) === text) return;
      let caret: number | null = null;
      try {
        caret = saveCaret(el);
      } catch {
        caret = null;
      }
      writeEditor(el, text);
      if (caret === null) return;
      try {
        restoreCaret(el, Math.min(caret, text.length));
      } catch {
        // 復元に失敗しても本文反映は済んでいるため無視する。
      }
    },
    [readEditor, saveCaret, writeEditor, restoreCaret]
  );

  // ---- Undo/Redo（markdown）----
  const recordUndoSnapshot = useCallback(
    (text: string, caret: number, kind: "type" | "struct") => {
      if (!isMarkdown || applyingHistoryRef.current) return;
      // 連続タイプは 1 ステップへコアレス（タイプ列の先頭状態だけを積む）。
      if (kind === "type" && lastOpKindRef.current === "type") return;
      undoRef.current.push({ caret, text });
      redoRef.current = [];
      lastOpKindRef.current = kind;
    },
    [isMarkdown]
  );

  const performUndo = useCallback(() => {
    if (!isMarkdown) return;
    const el = elementRef.current;
    if (!el || undoRef.current.length === 0) return;
    const now = Date.now();
    if (now - lastHistoryTsRef.current < HISTORY_DEDUP_MS) return; // keydown/beforeinput の二重発火防止
    lastHistoryTsRef.current = now;
    const curText = serializeMarkdownEditorDom(el);
    const curCaret = saveEditorCaretOffset(el) ?? curText.length;
    redoRef.current.push({ caret: curCaret, text: curText });
    const prev = undoRef.current.pop();
    if (!prev) return;
    applyingHistoryRef.current = true;
    buildMarkdownEditorDom(el, prev.text);
    restoreEditorCaretOffset(el, prev.caret);
    applyingHistoryRef.current = false;
    lastOpKindRef.current = "none";
    scheduleSend(prev.text);
    onLiveText?.(prev.text);
  }, [isMarkdown, elementRef, scheduleSend, onLiveText]);

  const performRedo = useCallback(() => {
    if (!isMarkdown) return;
    const el = elementRef.current;
    if (!el || redoRef.current.length === 0) return;
    const now = Date.now();
    if (now - lastHistoryTsRef.current < HISTORY_DEDUP_MS) return;
    lastHistoryTsRef.current = now;
    const curText = serializeMarkdownEditorDom(el);
    const curCaret = saveEditorCaretOffset(el) ?? curText.length;
    undoRef.current.push({ caret: curCaret, text: curText });
    const next = redoRef.current.pop();
    if (!next) return;
    applyingHistoryRef.current = true;
    buildMarkdownEditorDom(el, next.text);
    restoreEditorCaretOffset(el, next.caret);
    applyingHistoryRef.current = false;
    lastOpKindRef.current = "none";
    scheduleSend(next.text);
    onLiveText?.(next.text);
  }, [isMarkdown, elementRef, scheduleSend, onLiveText]);

  // 横取りした純粋変換の結果を適用する: Undo 記録 → 正準再構築 → キャレット復元 → 送信。
  // kind="type"（通常入力）は連続を 1 Undo ステップへコアレスし、"struct"（改行・削除・貼付等）は都度記録する。
  const commitTransform = useCallback(
    (
      el: HTMLElement,
      prevText: string,
      prevCaret: number,
      result: EditorTextAndCaret,
      kind: "type" | "struct"
    ) => {
      recordUndoSnapshot(prevText, prevCaret, kind);
      buildMarkdownEditorDom(el, result.text);
      restoreEditorCaretOffset(el, result.caret);
      scheduleSend(result.text);
      onLiveText?.(result.text);
    },
    [recordUndoSnapshot, scheduleSend, onLiveText]
  );

  // ネイティブ委任した入力（通常タイプ・単語削除など）の後始末: 行分類が変わったときだけ正準再構築し、
  // 生テキストを送信する。分類不変なら再構築せずキャレット/IME のネイティブ挙動を温存する。
  const reconcileAfterNativeInput = useCallback(
    (el: HTMLElement) => {
      const raw = serializeMarkdownEditorDom(el);
      if (editorNeedsRebuild(el, raw)) {
        const caret = saveEditorCaretOffset(el);
        buildMarkdownEditorDom(el, raw);
        if (caret !== null) restoreEditorCaretOffset(el, Math.min(caret, raw.length));
      }
      scheduleSend(raw);
      onLiveText?.(raw);
    },
    [scheduleSend, onLiveText]
  );

  // 編集モードに入った直後は要素にフォーカスし、初期テキストを反映する。
  //
  // 競合防止: editing 中に他者編集や自分の debounce echo で `initialText` が変動しても
  // ローカル入力を上書きしないよう、最新値は ref に保持して effect の依存からは外す。
  // 結果として「isEditing が false→true へ遷移した瞬間」だけ初期化され、編集中の
  // 外部更新は無視される（他者の最新値は editing 終了後の再レンダーで反映される）。
  const initialTextRef = useRef(initialText);
  initialTextRef.current = initialText;
  useEffect(() => {
    if (!isEditing) return;
    const el = elementRef.current;
    if (!el) return;
    const initial = initialTextRef.current;
    if (isMarkdown) {
      // markdown は空本文でも必ず正準ブロック（空なら blank ブロック）を構築する。
      // readEditor(空 el)==="" となり initial==="" と一致してしまうと build がスキップされ、
      // ブロックが 1 つも無い contentEditable になって初回入力が裸ノード化する不具合を防ぐ。
      writeEditor(el, initial);
    } else if (readEditor(el) !== initial) {
      writeEditor(el, initial);
    }
    // 編集開始時点のテキストを「同期済み」の基準にする（この後の収束判定の起点）。
    lastSyncedTextRef.current = initial;
    // Undo/Redo スタックを編集開始時にクリアする（markdown のみ使用）。
    undoRef.current = [];
    redoRef.current = [];
    lastOpKindRef.current = "none";
    // 前回の編集セッションの keydown 記録を持ち越さない（markdown のみ使用）。
    lastKeydownWasImeRef.current = false;
    el.focus();
    if (isMarkdown) {
      // キャレットを本文末尾へ（正準ブロック内のテキストノードに置く）。
      // el 直下（ブロックの外）に置くと、最初のキー入力がブロック外の裸テキストノードとして挿入され、
      // serialize（直下の要素ブロックのみ走査）に取りこぼされて 1 文字目が消える不具合になる。
      restoreEditorCaretOffset(el, initial.length);
    } else {
      // キャレットを末尾に
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditing, elementRef, readEditor, writeEditor, isMarkdown]);

  // 同時編集の収束（エピックボードと同じ「全文 LWW リレー + idle 時のみ適用」の設計を流用）。
  // 編集中に他者やサーバの確定で initialText（= object.text の権威値）が変化したとき、ローカルに
  // 未送信の変更が無ければ（＝現在の DOM が最後に同期した内容と一致していれば）、その最新テキストを
  // キャレットを保ったまま反映する。未送信変更がある間は反映せず、自分の debounce 送信が
  // 全文 LWW で勝つ（結果として双方が最終的に同じ内容へ収束する）。
  // これにより「同じテキスト図形を複数人が同時に開いて編集し、互いの入力が反映される」体験になる。
  useEffect(() => {
    if (!isEditing) return;
    const el = elementRef.current;
    if (!el) return;
    // 自分の送信エコーを含め、同期済みと同じなら何もしない。
    if (initialText === lastSyncedTextRef.current) return;
    // 未送信の debounce 送信が保留中の間は「idle でない」とみなし、リモートを適用しない。
    // DOM 上は一時的に同期済みと一致していても（入力してすぐ元へ戻した直後など）、保留中の timer が
    // 後から古いテキストを送り返して、先に適用したリモート更新を巻き戻してしまうため。
    // timer が確定（発火）して完全に idle になってから、次のリモート更新で適用する。
    if (timerRef.current !== null) return;
    // ローカルに未送信の変更がある（diverged）間は上書きしない（自分の送信が LWW で反映される）。
    if (readEditor(el) !== lastSyncedTextRef.current) return;
    applyRemoteText(el, initialText);
    lastSyncedTextRef.current = initialText;
    // リモート適用後は自前 Undo/Redo をリセットする（Undo が旧ローカル状態へ戻り、
    // その差分が LWW で相手の変更を上書きしてしまうのを防ぐ）。
    undoRef.current = [];
    redoRef.current = [];
    lastOpKindRef.current = "none";
  }, [isEditing, initialText, elementRef, readEditor, applyRemoteText]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const enterEditMode = useCallback(() => {
    if (isDisabled) return;
    isEditingRef.current = true;
    setEditing(true);
  }, [isDisabled]);
  const leaveEditMode = useCallback(() => {
    // 編集中でなければ何もしない（最重要ガード）。
    // 非編集時の本文は React 管理の <a> / <span>（LinkifiedText）で描画されており、リンクは
    // フォーカスを持てるため、リンククリック（新規タブで開く）や Escape で、編集していないのに
    // onBlur / onKeyDown 経由で本関数へ到達し得る。そのまま進むと下の textContent = "" が
    // React 管理下の子ノードを消して「本文表示が消える」「次の reconcile で removeChild が
    // NotFoundError で落ちてエラー画面になる」という不具合になる（v0.2.79 で顕在化した実障害）。
    if (!isEditingRef.current) return;
    isEditingRef.current = false;
    const el = elementRef.current;
    const text = el ? readEditor(el) : "";
    // 編集終了時に、contentEditable 中にブラウザが作った DOM ノード（テキスト・改行の <div>/<br>）を
    // 明示的に消してから非編集表示へ戻す。非編集表示はテキストを <span>/<a>（要素）で描画するため、
    // React は「以前の子＝null」からの差分で要素を "追記" するだけで、この未追跡ノードを消さない。
    // 結果として旧テキストと新表示が二重化する（要素の子は単一テキスト子のような textContent 置換に
    // ならないため）。ここで空にしておけば、React は空の要素へ非編集表示を入れるだけで二重化しない。
    if (el) el.textContent = "";
    setEditing(false);
    // 0 文字（空白のみを含む）で確定した場合、テキスト図形はキャンバス上で見えなくなり
    // 位置も分からなくなるため、対象コールバック（テキスト図形の自動削除）へ委ねる。
    // このとき空の text.update は送らず、保留中の送信も破棄する。
    if (onEmptyCommit && text.trim().length === 0) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      onEmptyCommit();
      return;
    }
    if (el) {
      flushPending(text);
    }
  }, [elementRef, flushPending, onEmptyCommit, readEditor]);

  const onInput = useCallback(
    (event: React.FormEvent<HTMLElement>) => {
      if (composingRef.current) return;
      const el = event.currentTarget as HTMLElement;
      if (isMarkdown) {
        // 構造変更は beforeinput で横取り済み。ここへ来るのはネイティブ委任した通常入力の後始末。
        reconcileAfterNativeInput(el);
        return;
      }
      const text = readEditorText(el);
      scheduleSend(text);
      // 付箋など、入力中も表示（フォントサイズ）を本文へ追従させたい種別へ通知する。
      onLiveText?.(text);
    },
    [isMarkdown, reconcileAfterNativeInput, scheduleSend, onLiveText]
  );

  const onCompositionStart = useCallback(() => {
    composingRef.current = true;
    if (!isMarkdown) return;
    // IME 変換開始時点（変換前）の状態を Undo の起点として記録する。
    const el = elementRef.current;
    if (!el) return;
    const sel = saveEditorSelection(el);
    recordUndoSnapshot(serializeMarkdownEditorDom(el), sel?.start ?? 0, "type");
  }, [isMarkdown, elementRef, recordUndoSnapshot]);

  const onCompositionEnd = useCallback(
    (event: React.CompositionEvent<HTMLElement>) => {
      composingRef.current = false;
      compositionEndedAtRef.current = Date.now();
      const el = event.currentTarget as HTMLElement;
      if (isMarkdown) {
        // IME 変換確定後に整形を反映する（変換中は composing ガードで再構築しない）。
        reconcileAfterNativeInput(el);
        return;
      }
      const text = readEditorText(el);
      scheduleSend(text);
      // IME 変換確定時にも最新本文で通知する（変換中は onInput の composing ガードで呼ばれない）。
      onLiveText?.(text);
    },
    [isMarkdown, reconcileAfterNativeInput, scheduleSend, onLiveText]
  );

  const onBlur = useCallback(() => {
    leaveEditMode();
  }, [leaveEditMode]);

  // 編集中の自分のキャレット位置（本文先頭からの文字オフセット）を他参加者へ中継する。
  // saveCaretCharacterOffset は innerText 基準（改行 "\n" を含む）でオフセットを求めるため、
  // 受信側（object.text を描画した DOM）で同じ文字数の位置に Range を作れば位置が一致する。
  // markdown モードでは saveEditorCaretOffset（生テキスト基準）を用いる（従来と同じ生オフセット座標系）。
  const caretThrottleRef = useRef<{
    lastAt: number;
    timer: ReturnType<typeof setTimeout> | null;
  }>({ lastAt: 0, timer: null });
  const broadcastCaretNow = useCallback(() => {
    if (isDisabled) return;
    if (composingRef.current) return; // IME 変換中は送らない（確定時の selectionchange で送る）
    const el = elementRef.current;
    if (!el) return;
    const offset = saveCaret(el);
    if (offset === null) return;
    dispatcher.previewTextCursor(objectId, offset);
  }, [isDisabled, elementRef, dispatcher, objectId, saveCaret]);
  const scheduleBroadcastCaret = useCallback(() => {
    const state = caretThrottleRef.current;
    const now = Date.now();
    const elapsed = now - state.lastAt;
    if (elapsed >= CARET_BROADCAST_INTERVAL_MS) {
      state.lastAt = now;
      broadcastCaretNow();
      return;
    }
    if (state.timer) return; // 末尾送信を既に予約済み
    state.timer = setTimeout(() => {
      state.timer = null;
      caretThrottleRef.current.lastAt = Date.now();
      broadcastCaretNow();
    }, CARET_BROADCAST_INTERVAL_MS - elapsed);
  }, [broadcastCaretNow]);

  // 編集中は selectionchange を購読し、キャレット / 選択が自分の編集要素内で動いたら位置を中継する。
  // タイプ・矢印キー・クリックのいずれでもキャレット移動時に発火するため、これ 1 つで網羅できる。
  useEffect(() => {
    if (!isEditing) return undefined;
    const onSelectionChange = (): void => {
      const el = elementRef.current;
      const selection = window.getSelection();
      if (!el || !selection || selection.rangeCount === 0) return;
      if (!el.contains(selection.getRangeAt(0).startContainer)) return;
      scheduleBroadcastCaret();
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", onSelectionChange);
      const throttle = caretThrottleRef.current;
      if (throttle.timer) {
        clearTimeout(throttle.timer);
        throttle.timer = null;
      }
    };
  }, [isEditing, elementRef, scheduleBroadcastCaret]);

  // 構造変更（Enter・改行・削除・履歴）を beforeinput で一元横取りする（markdown のみ）。
  // React の onBeforeInput は inputType の扱いにばらつきがあるため、ネイティブリスナで確実に受ける。
  const handleBeforeInput = useCallback(
    (event: Event) => {
      if (!isMarkdown) return;
      const e = event as InputEvent;
      const el = elementRef.current;
      if (!el) return;
      const type = e.inputType;
      if (type === "insertParagraph" || type === "insertLineBreak") {
        // IME 変換中の改行入力は「変換確定の Enter」であり改行意図ではないため、ネイティブに
        // 委任せず破棄する。委任するとブラウザのブロック分割が bullet div を data-prefix ごと
        // クローンし（マーカー span はキャレット前方のため複製されない）、直後の serialize が
        // 幻の空箇条書きを具現化して「確定 Enter だけでリスト継続」になってしまう。
        // ※ cancelable=false の環境では preventDefault は no-op だが、その場合は serialize 側の
        //    正準構造チェック（span を欠く bullet の data-prefix を信用しない）が受け皿になる。
        if (composingRef.current || e.isComposing) {
          lastKeydownWasImeRef.current = false;
          e.preventDefault();
          return;
        }
        // 変換確定の Enter が isComposing=false で漏れてきた場合の識別。直前の keydown が
        // IME 変換中の発火（Windows Chrome では key="Process"）なら、この改行入力は確定の漏れで
        // あり、着弾が確定処理の遅延でどれだけ遅れても破棄する。破棄はどの分岐でも one-shot で
        // 記録を消費し、keydown を伴わない改行入力（音声入力・仮想キーボード等）を stale な記録で
        // 恒常的に破棄しない。
        if (lastKeydownWasImeRef.current) {
          lastKeydownWasImeRef.current = false;
          e.preventDefault();
          return;
        }
        // Safari 型の漏れ（compositionend 後の keydown 自体が本物の Enter と区別できない）向けの
        // 既存ガード。確定直後 50ms 以内の本物 Enter も巻き込む既知のトレードオフは受容する
        // （改善余地: この壁時計窓は Safari 型経路の受け皿でしかないため、将来 UA/挙動判定で
        //   Safari 系に限定すれば他ブラウザの取りこぼしを無くせる）。
        if (Date.now() - compositionEndedAtRef.current < COMPOSITION_ENTER_GUARD_MS) {
          lastKeydownWasImeRef.current = false;
          e.preventDefault();
          return;
        }
      }
      if (composingRef.current || e.isComposing) return; // IME 中は（改行以外を）ネイティブに委ねる
      if (type === "historyUndo") {
        e.preventDefault();
        performUndo();
        return;
      }
      if (type === "historyRedo") {
        e.preventDefault();
        performRedo();
        return;
      }

      const targetRanges = typeof e.getTargetRanges === "function" ? e.getTargetRanges() : [];
      const firstRange = targetRanges[0];
      const collapsedSingle = targetRanges.length === 1 && !!firstRange && firstRange.collapsed;

      const raw = serializeMarkdownEditorDom(el);
      // オートコレクト（insertReplacementText）は見た目の Selection が collapsed でも対象範囲が語になる
      // ため、対象 Range を優先して生オフセットへ変換する。
      const sel = firstRange
        ? (rawSelectionFromRange(el, firstRange) ?? saveEditorSelection(el))
        : saveEditorSelection(el);

      // 削除をネイティブに委任してよいのは「対象が本文テキストノード内・単一 collapsed・境界でない」場合のみ。
      // 空 blank ブロック（<br>）等の要素上の collapsed でネイティブ削除させると <br> が消えて空行高さ・
      // キャレットアンカーが壊れるため、テキストノード上でない削除は横取りして正準再構築で扱う。
      const deleteInTextNode =
        collapsedSingle && firstRange?.startContainer.nodeType === Node.TEXT_NODE;
      if (
        deleteInTextNode &&
        sel &&
        (type === "deleteContentBackward" || type === "deleteWordBackward") &&
        !isBackspaceBoundary(raw, sel.start)
      ) {
        recordUndoSnapshot(raw, sel.start, "type");
        return;
      }
      if (
        deleteInTextNode &&
        sel &&
        (type === "deleteContentForward" || type === "deleteWordForward") &&
        !isDeleteForwardBoundary(raw, sel.start)
      ) {
        recordUndoSnapshot(raw, sel.start, "type");
        return;
      }

      // それ以外（通常入力 insertText・改行・境界削除・範囲削除など）は横取りして純粋変換で適用する。
      // 通常入力もネイティブに委ねると空ブロックへの初回入力で裸テキストノードが生じ 1 文字目を
      // 取りこぼす（contentEditable 既知の不安定挙動）ため、入力もこちらで確実に制御する。
      e.preventDefault();
      if (!sel) return;
      const result = transformForInput(type, raw, sel, e.data);
      if (!result) return;
      const kind: "type" | "struct" =
        type === "insertText" ||
        type === "insertReplacementText" ||
        type === "insertFromComposition"
          ? "type"
          : "struct";
      commitTransform(el, raw, sel.start, result, kind);
    },
    [isMarkdown, elementRef, performUndo, performRedo, recordUndoSnapshot, commitTransform]
  );

  useEffect(() => {
    if (!isMarkdown || !isEditing) return undefined;
    const el = elementRef.current;
    if (!el) return undefined;
    el.addEventListener("beforeinput", handleBeforeInput);
    return () => el.removeEventListener("beforeinput", handleBeforeInput);
  }, [isMarkdown, isEditing, elementRef, handleBeforeInput]);

  // 貼り付け:
  // - preserveHtmlAnchorsOnPaste 有効時は、リンク付き HTML を [表示テキスト](URL) へ変換して取り込む。
  // - markdown 編集では挿入を自前制御する（裸ノード化を避ける）。HTML アンカーがあれば変換結果を、
  //   無ければ text/plain を選択範囲へ挿入する。
  // - 非 markdown（セクション等）では従来どおり、変換できた場合のみ execCommand で挿入し、
  //   それ以外は既定の貼り付けに任せる。
  const onPaste = useCallback(
    (event: React.ClipboardEvent<HTMLElement>) => {
      if (!isEditing) return;
      const el = elementRef.current;
      if (!el) return;
      const converted = preserveHtmlAnchorsOnPaste
        ? htmlFragmentToLinkText(event.clipboardData.getData("text/html"))
        : undefined;

      if (isMarkdown) {
        event.preventDefault();
        const text = converted ?? event.clipboardData.getData("text/plain");
        const sel = saveEditorSelection(el);
        if (!sel) return;
        const raw = serializeMarkdownEditorDom(el);
        commitTransform(el, raw, sel.start, insertTextAt(raw, sel.start, sel.end, text), "struct");
        return;
      }

      // 非 markdown: アンカーを含む HTML のときだけ変換して挿入（それ以外は既定の貼り付けに任せる）。
      if (converted === undefined) return;
      event.preventDefault();
      // execCommand はブラウザのネイティブ undo スタックへ記録され、input イベント（onInput）も発火する。
      if (
        typeof document.execCommand === "function" &&
        document.execCommand("insertText", false, converted)
      ) {
        return;
      }
      // 非対応環境では Selection API で挿入し、input イベントを発火して onInput へ反映する。
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (!el.contains(range.startContainer)) return;
      range.deleteContents();
      const textNode = document.createTextNode(converted);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      el.dispatchEvent(new Event("input", { bubbles: true }));
    },
    [isEditing, isMarkdown, elementRef, preserveHtmlAnchorsOnPaste, commitTransform]
  );

  // カット: 選択文字列を clipboard へ書いてから（①）純粋変換で範囲削除する（②）。既定削除は preventDefault で
  // 止めるため、削除は自前変換の 1 回のみ（ネイティブ削除との二重適用なし）。
  const onCut = useCallback(
    (event: React.ClipboardEvent<HTMLElement>) => {
      if (!isMarkdown) return;
      const el = elementRef.current;
      if (!el) return;
      event.preventDefault();
      const sel = saveEditorSelection(el);
      if (!sel || sel.start === sel.end) return;
      const raw = serializeMarkdownEditorDom(el);
      event.clipboardData.setData("text/plain", raw.slice(sel.start, sel.end));
      commitTransform(el, raw, sel.start, deleteRange(raw, sel.start, sel.end), "struct");
    },
    [isMarkdown, elementRef, commitTransform]
  );

  // ドロップ: プレーンテキストをドロップ位置へ挿入する（既定は無効化し、純粋変換で正準再構築する）。
  // 空テキスト（画像など）のドロップは no-op。ドロップ位置は caretRangeFromPoint で求め、取れなければ
  // 現在の選択位置へ挿入する。
  const onDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!isMarkdown) return;
      const el = elementRef.current;
      if (!el) return;
      event.preventDefault();
      const text = event.dataTransfer.getData("text/plain");
      if (!text) return;
      const raw = serializeMarkdownEditorDom(el);
      const doc = el.ownerDocument as Document & {
        caretRangeFromPoint?: ((x: number, y: number) => Range | null) | undefined;
      };
      const dropRange = doc.caretRangeFromPoint?.(event.clientX, event.clientY) ?? null;
      const sel =
        (dropRange && el.contains(dropRange.startContainer)
          ? rawSelectionFromRange(el, dropRange)
          : null) ?? saveEditorSelection(el);
      if (!sel) return;
      commitTransform(el, raw, sel.start, insertTextAt(raw, sel.start, sel.end, text), "struct");
    },
    [isMarkdown, elementRef, commitTransform]
  );

  // 内部テキストのドラッグ移動は無効化する（onDrop は外部ドロップの挿入のみを担う）。
  // 内部 drag&drop を許すと、元選択の削除と drop 位置への挿入の整合を取る必要があり、
  // onDrop の単純挿入だけでは元テキストが複製されてしまうため、ドラッグ開始自体を止める。
  const onDragStart = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      if (!isMarkdown) return;
      event.preventDefault();
    },
    [isMarkdown]
  );

  // 編集中（contentEditable）に Ctrl/Cmd+クリックで、クリック位置の URL を新規タブで開く。
  // クリックでキャレットが置かれた位置の文字オフセットを求め、その位置に重なる URL を判定する。
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!isEditing) return;
      if (!(event.ctrlKey || event.metaKey)) return;
      const el = elementRef.current;
      if (!el) return;
      const offset = saveCaret(el);
      if (offset === null) return;
      const url = findUrlTokenAt(readEditor(el), offset);
      if (url && isHttpUrl(url)) {
        event.preventDefault();
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [isEditing, elementRef, saveCaret, readEditor]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        leaveEditMode();
        return;
      }
      if (!isMarkdown) {
        // 非 markdown（セクション等）では Enter キーはブラウザ既定の改行挿入に委ねる（自前で "\n"
        // テキストノードを挿入すると、末尾改行がうまく表示・配置されない contentEditable 既知の問題に
        // 当たるため）。改行の取得は readEditorText（innerText 経由）で行うので、ブラウザが <div> / <br>
        // を挿入しても改行は確実に object.text へ反映される。
        return;
      }
      // ここから markdown モードのキー操作。Enter/改行/削除は beforeinput で横取りするため、ここでは
      // Tab（箇条書きのネスト）と Undo/Redo を扱う。
      // 毎 keydown で「IME 変換中の発火か」を記録する（beforeinput 側の漏れ Enter 識別用）。
      // 確定 Enter は Windows Chrome で key="Process"（keyCode 229）になるためキー名では判定できず、
      // 変換中であること自体を記録する。素の keyCode 229 を単独条件にしないのは、Android 系が
      // 非変換中の実 Enter でも 229 を送る環境があり、実 Enter を全滅させ得るため。
      // ※ 下の composing 早期 return より前に置くこと（後に置くと肝心の IME 由来 keydown が記録されない）。
      lastKeydownWasImeRef.current = composingRef.current || event.nativeEvent.isComposing;
      // IME 変換中（keyCode 229 を含む）は横取りしない。
      if (
        composingRef.current ||
        event.nativeEvent.isComposing ||
        event.nativeEvent.keyCode === 229
      ) {
        return;
      }
      const el = elementRef.current;
      if (event.key === "Tab") {
        event.preventDefault();
        if (!el) return;
        const sel = saveEditorSelection(el);
        if (!sel) return;
        const raw = serializeMarkdownEditorDom(el);
        const result = changeBulletIndent(
          raw,
          sel.start,
          sel.end,
          event.shiftKey ? -1 : 1,
          BULLET_MAX_LEVEL
        );
        if (result) commitTransform(el, raw, sel.start, result, "struct");
        return;
      }
      const mod = event.ctrlKey || event.metaKey;
      if (mod && (event.key === "z" || event.key === "Z")) {
        event.preventDefault();
        if (event.shiftKey) performRedo();
        else performUndo();
        return;
      }
      if (mod && (event.key === "y" || event.key === "Y")) {
        event.preventDefault();
        performRedo();
      }
    },
    [isMarkdown, elementRef, leaveEditMode, commitTransform, performUndo, performRedo]
  );

  const onKeyUp = useCallback(() => {
    if (!isMarkdown) return;
    // keydown 記録（lastKeydownWasImeRef）はその物理キーストロークが生む beforeinput 列（漏れ確定
    // Enter を含む）までが有効期間。keyup で消費し、候補クリック等 Enter を伴わず composition が
    // 終了した後に届く keydown レスの改行入力（音声入力・仮想キーボード等）を stale な記録で
    // 破棄しないようにする。漏れ確定 Enter の beforeinput は同一キーストロークの keyup より
    // 前に着弾するため、ここでの消費は時間窓に依存しない漏れ Enter 破棄を弱めない。
    lastKeydownWasImeRef.current = false;
  }, [isMarkdown]);

  return {
    isEditing,
    enterEditMode,
    leaveEditMode,
    editorProps: {
      contentEditable: isEditing,
      suppressContentEditableWarning: true,
      onInput,
      onCompositionStart,
      onCompositionEnd,
      onBlur,
      onKeyDown,
      onKeyUp,
      onClick,
      onPaste,
      onCut,
      onDrop,
      onDragStart,
    },
  };
}

/** inputType（横取り対象）を生テキスト + 選択の純粋変換へ写像する。未知の構造入力は正準化のみ。 */
function transformForInput(
  inputType: string,
  raw: string,
  sel: EditorSelection,
  data: string | null
): EditorTextAndCaret | null {
  switch (inputType) {
    case "insertParagraph":
      return splitForEnter(raw, sel.start, sel.end);
    case "insertLineBreak":
      return insertPlainLineBreak(raw, sel.start, sel.end);
    case "insertText":
    case "insertReplacementText":
    case "insertFromComposition":
      return insertTextAt(raw, sel.start, sel.end, data ?? "");
    case "insertFromPaste":
    case "insertFromDrop":
      // 通常は onPaste / onDrop（React 合成）が先に横取りするため到達しないが、防御的に無視する。
      return null;
    case "deleteContentBackward":
    case "deleteWordBackward":
    case "deleteByCut":
    case "deleteContent":
      return sel.start !== sel.end
        ? deleteRange(raw, sel.start, sel.end)
        : deleteBackward(raw, sel.start, BULLET_MAX_LEVEL);
    case "deleteContentForward":
    case "deleteWordForward":
      return sel.start !== sel.end
        ? deleteRange(raw, sel.start, sel.end)
        : deleteForward(raw, sel.start);
    case "deleteByDrag":
      return deleteRange(raw, sel.start, sel.end);
    default:
      // 未知の構造入力は現在の生テキストで正準化のみ（安全側）。
      return { caret: sel.start, text: raw };
  }
}

/**
 * contentEditable の表示テキストを改行付きで取得する。
 *
 * Enter キーでブラウザが挿入する <div> / <br> を textContent は改行へ変換せず連結するため、
 * textContent では改行が失われてしまう（要望②の改行が反映されない不具合の原因）。
 * innerText は「実際に表示されているテキスト」を返し、<div> / <br> 由来の改行を "\n" として
 * 含むため、white-space: pre-wrap の表示と一致した文字列（object.text へ保存する値）を得られる。
 * innerText 非対応の環境（jsdom 等のテスト環境）では textContent にフォールバックする。
 */
function readEditorText(el: HTMLElement): string {
  const rendered = (el as { innerText?: string | undefined }).innerText;
  return typeof rendered === "string" ? rendered : (el.textContent ?? "");
}

/**
 * 選択開始位置を、要素先頭からの文字数として返す。フォーカスが要素外なら null。
 *
 * 復元側（restoreCaretCharacterOffset）は textContent（改行 "\n" を含む）を走査して数えるが、
 * 保存側で Range.toString() を使うと <div> / <br> の改行を数えず、複数行で桁がずれる（保存と復元で
 * 改行のカウント方法が食い違うため）。これを避けるため、キャレット位置へ一時マーカーを挿入し、
 * readEditorText（innerText 経由で改行を "\n" として含む）上でのインデックスを求めてから元へ戻す。
 * これにより保存・復元が同じ座標系（改行込み）になり、複数行でもキャレット位置が保たれる。
 */
function saveCaretCharacterOffset(el: HTMLElement): number | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!el.contains(range.startContainer)) return null;
  // Unicode 私用領域の 1 文字。通常テキストに現れないため、キャレット位置マーカーとして使う。
  const MARKER = "\uE000";
  const markerNode = document.createTextNode(MARKER);
  const insertRange = range.cloneRange();
  insertRange.collapse(true);
  insertRange.insertNode(markerNode);
  try {
    const rendered = readEditorText(el);
    const index = rendered.indexOf(MARKER);
    return index >= 0 ? index : null;
  } finally {
    // マーカーを除去し、分割されたテキストノードを normalize で元通りに結合する。
    markerNode.remove();
    el.normalize();
  }
}

/**
 * 要素先頭から offset 文字目へキャレットを移動する。
 * textContent 置換後の単一テキストノードを主に想定しつつ、複数テキストノードでも走査して復元する。
 */
function restoreCaretCharacterOffset(el: HTMLElement, offset: number): void {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  if (!node) {
    // テキストノードが無い（空）の場合は要素先頭へ。
    range.selectNodeContents(el);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return;
  }
  let remaining = offset;
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (remaining <= len) {
      range.setStart(node, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= len;
    const next = walker.nextNode();
    if (!next) {
      // offset が末尾を超える場合は最後のノード末尾へ寄せる。
      range.setStart(node, len);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    node = next;
  }
}
